import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';

interface LanguageSwitcherProps {
  /**
   * `dark` staat op een donkere ondergrond (de voettekst van de spelerspagina),
   * `light` op een witte kaart (instellingen).
   */
  variant?: 'dark' | 'light';
  className?: string;
}

/**
 * Taalkeuze als klein segmented control.
 *
 * Staat bewust ook op de publieke spelerspagina: die route heeft geen inlog, dus een
 * Engelstalige ouder die de opstelling toegestuurd krijgt kan de taal nergens anders
 * omzetten. `changeLanguage` schrijft de keuze naar localStorage, dus hij overleeft
 * een herlaadbeurt.
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'light',
  className = '',
}) => {
  const { t, i18n } = useTranslation('common');
  const active = (i18n.resolvedLanguage ?? i18n.language)?.split('-')[0];

  return (
    <div
      role="group"
      aria-label={t('language.label')}
      className={clsx(
        'inline-flex overflow-hidden rounded-md border',
        variant === 'dark' ? 'border-white/25' : 'border-gray-300',
        className,
      )}
    >
      {SUPPORTED_LANGUAGES.map((language: SupportedLanguage) => {
        const isActive = active === language;

        return (
          <button
            key={language}
            type="button"
            lang={language}
            onClick={() => i18n.changeLanguage(language)}
            aria-pressed={isActive}
            title={t('language.switchTo', { language: t(`language.${language}`) })}
            className={clsx(
              'px-3 py-1 text-xs font-medium transition-colors',
              isActive && 'bg-hockey-blue text-white',
              !isActive && variant === 'dark' && 'text-white/75 hover:bg-white/10',
              !isActive && variant === 'light' && 'text-gray-600 hover:bg-gray-100',
            )}
          >
            {t(`language.${language}Short`)}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
